import type { Repo } from "@automerge/automerge-repo";
import { RepoContext } from "@automerge/react";
import { bytesUri } from "playground/fs/blob-doc";
import type { PlayFileInode } from "playground/fs/types";
import { useContext, useEffect, useState } from "react";

import { PROJECT_FILE_PROXY_PATH } from "@/components/ProjectFileLink";

/**
 * A URL the browser can fetch a blob file's bytes from, following an
 * annotated file's doc to them. Undefined for a file that is its own doc.
 */
export async function blobSrc(
  repo: Repo,
  inode: PlayFileInode,
): Promise<string | undefined> {
  const uri = await bytesUri(repo, inode);
  if (uri === null) return undefined;
  return uri.startsWith("s3://")
    ? `${PROJECT_FILE_PROXY_PATH}?source=${encodeURIComponent(uri)}`
    : uri;
}

/** {@link blobSrc} for a component: null until it has resolved. */
export function useBlobSrc(
  inode: PlayFileInode | undefined,
): { src: string | undefined } | null {
  const repo = useContext(RepoContext);
  const [resolved, setResolved] = useState<{ src: string | undefined } | null>(
    null,
  );
  const key = JSON.stringify([inode?.uri, inode?.version]);
  useEffect(() => {
    if (!repo || !inode) {
      setResolved({ src: undefined });
      return;
    }
    let live = true;
    setResolved(null);
    void blobSrc(repo, inode).then((src) => {
      if (live) setResolved({ src });
    });
    return () => {
      live = false;
    };
    // `key` is the inode's identity; the object itself is rebuilt on any change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, key]);
  return resolved;
}
